import type { InjectionKey } from 'vue';
import type { EventHookRegister } from '#layers/base/public';
import type { OnBlockTreeSlotClickPayload } from '#layers/block-engine/composables/block-editor/tree';
import type { BlockSerializedChild } from '#layers/block-engine/types/block';
import type { InsertBlockOptions, MoveBlockOptions } from './context';
import { inject } from 'vue';

export interface BlockProgrammaticChildRegistration {
	child: BlockSerializedChild;
	parentChildId: string;
	slotMap?: Record<string, string>;
	skipInParentChain?: boolean;
	hiddenInBlockTree?: boolean;
	selectable?: boolean;
	draggable?: boolean;
	inspectable?: boolean;
	showSelectionUi?: boolean;
}

export interface BlockProgrammaticChildAdapter<BlockProps extends Record<string, any> = Record<string, any>> {
	getChild: () => BlockSerializedChild;
	slotIsEmpty: (slot?: string) => boolean;
	getSlot: (name: string) => BlockSerializedChild[];
	getSlotNames: () => string[];
	removeSlotChildren: (name: string) => void;
	onBlockTreeSlotClick: EventHookRegister<Omit<OnBlockTreeSlotClickPayload, 'childId'>>;
	getProps: () => BlockProps;
	setProp: <K extends keyof BlockProps>(prop: K, value: BlockProps[K]) => void;
	insertBlock: (options: InsertBlockOptions) => Promise<BlockSerializedChild>;
	moveBlock: (options: MoveBlockOptions) => void;
	isPreview: boolean;
	triggerLayoutUpdate: () => void;
}

export const blockProgrammaticChildAdapterKey = Symbol('blockProgrammaticChildAdapterKey') as InjectionKey<BlockProgrammaticChildAdapter>;

/** Returns the injected adapter only for the child that owns its virtual identity. */
export function useBlockProgrammaticChildAdapter<BlockProps extends Record<string, any> = Record<string, any>>(
	getChild: () => BlockSerializedChild,
) {
	const adapter = inject(blockProgrammaticChildAdapterKey, null) as BlockProgrammaticChildAdapter<BlockProps> | null;

	// Persisted slot descendants inherit injections but rebind their own child identity.
	return adapter?.getChild().id === getChild().id ? adapter : null;
}

export function resolveBlockProgrammaticChildRegistration(
	registry: Map<string, BlockProgrammaticChildRegistration> | undefined,
	childId: string | undefined,
) {
	if (!registry || !childId) {
		return null;
	}

	return registry.get(childId) ?? null;
}

export function isBlockProgrammaticChildHiddenInTree(
	registry: Map<string, BlockProgrammaticChildRegistration> | undefined,
	childId: string | undefined,
) {
	const registration = resolveBlockProgrammaticChildRegistration(registry, childId);
	return registration ? registration.hiddenInBlockTree !== false : false;
}

export function isBlockProgrammaticChildSelectable(
	registry: Map<string, BlockProgrammaticChildRegistration> | undefined,
	childId: string | undefined,
) {
	const registration = resolveBlockProgrammaticChildRegistration(registry, childId);
	return registration ? registration.selectable === true : true;
}

export function isBlockProgrammaticChildDraggable(
	registry: Map<string, BlockProgrammaticChildRegistration> | undefined,
	childId: string | undefined,
) {
	const registration = resolveBlockProgrammaticChildRegistration(registry, childId);
	return registration ? registration.draggable === true : true;
}

export function isBlockProgrammaticChildInspectable(
	registry: Map<string, BlockProgrammaticChildRegistration> | undefined,
	childId: string | undefined,
) {
	const registration = resolveBlockProgrammaticChildRegistration(registry, childId);
	return registration ? registration.inspectable === true : true;
}

export function shouldShowBlockProgrammaticChildSelectionUi(
	registry: Map<string, BlockProgrammaticChildRegistration> | undefined,
	childId: string | undefined,
) {
	const registration = resolveBlockProgrammaticChildRegistration(registry, childId);
	return registration ? registration.showSelectionUi === true : true;
}

export function resolveBlockProgrammaticChildTarget(
	registry: Map<string, BlockProgrammaticChildRegistration> | undefined,
	target?: {
		childId?: string;
		slotName?: string;
		index?: number;
	},
) {
	let resolvedTarget = target;
	let registration: BlockProgrammaticChildRegistration | null = null;

	while (resolvedTarget?.childId) {
		const currentRegistration = resolveBlockProgrammaticChildRegistration(registry, resolvedTarget.childId);

		if (!currentRegistration) {
			break;
		}

		registration ??= currentRegistration;

		const slotName = resolvedTarget.slotName == null
			? undefined
			: (currentRegistration.slotMap?.[resolvedTarget.slotName] ?? resolvedTarget.slotName);

		resolvedTarget = {
			...resolvedTarget,
			childId: currentRegistration.parentChildId,
			slotName,
		};
	}

	return {
		registration,
		target: resolvedTarget,
	};
}

export function resolveBlockProgrammaticChildParentChainTarget(
	registry: Map<string, BlockProgrammaticChildRegistration> | undefined,
	target?: {
		childId?: string;
		slotName?: string;
		index?: number;
	},
) {
	let resolvedTarget = target;

	while (resolvedTarget?.childId) {
		const registration = resolveBlockProgrammaticChildRegistration(registry, resolvedTarget.childId);

		if (!registration) {
			break;
		}

		if (!registration.skipInParentChain) {
			return {
				registration,
				target: resolvedTarget,
			};
		}

		const slotName = resolvedTarget.slotName == null
			? undefined
			: (registration.slotMap?.[resolvedTarget.slotName] ?? resolvedTarget.slotName);

		resolvedTarget = {
			...resolvedTarget,
			childId: registration.parentChildId,
			slotName,
		};
	}

	return {
		registration: null,
		target: resolvedTarget,
	};
}
